import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Globe, 
  Check, 
  ExternalLink, 
  Copy, 
  Info, 
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Link2,
  Shield
} from 'lucide-react';

// App deployment domain - this is where all custom subdomains should CNAME to
const APP_DEPLOYMENT_DOMAIN = 'app.davidhughesdesign.ca';

export default function PublishingSettings() {
  const { user, refreshUser } = useAuth();
  const { get, put, post, loading } = useApi();
  
  const [subdomain, setSubdomain] = useState('');
  const [subdomainPrefix, setSubdomainPrefix] = useState('decks');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [validationError, setValidationError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('not_connected'); // not_connected, pending, connected, invalid
  const [recentDecks, setRecentDecks] = useState([]);

  useEffect(() => {
    if (user?.publishing_domain) {
      setSubdomain(user.publishing_domain);
      // Extract prefix from saved domain
      const parts = user.publishing_domain.split('.');
      if (parts.length > 2) {
        setSubdomainPrefix(parts[0]);
      }
      // Check connection status on load
      if (user.publishing_domain) {
        setConnectionStatus(user.domain_connection_status || 'pending');
      }
    }
    fetchRecentDecks();
  }, [user]);

  const fetchRecentDecks = async () => {
    try {
      const archive = await get('/style-archive');
      setRecentDecks(archive.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch recent decks:', error);
    }
  };

  // Validate subdomain format - must have at least one dot and a prefix
  const validateSubdomain = (value) => {
    if (!value) {
      setValidationError('');
      return true;
    }
    
    // Clean the input
    const cleanValue = value.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase();
    
    // Check for subdomain format (must have prefix.domain.tld)
    const parts = cleanValue.split('.');
    if (parts.length < 3) {
      setValidationError('Please use a subdomain format (e.g., decks.yourcompany.com)');
      return false;
    }
    
    // Check if it looks like a root domain
    if (parts.length === 2) {
      setValidationError('Root domains are not supported. Please use a subdomain like decks.yourcompany.com');
      return false;
    }
    
    // Basic format validation
    const subdomainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+\.[a-z]{2,}$/;
    if (!subdomainRegex.test(cleanValue)) {
      setValidationError('Invalid domain format');
      return false;
    }
    
    setValidationError('');
    return true;
  };

  const handleSubdomainChange = (value) => {
    // Remove protocol and trailing slash
    let cleanDomain = value.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase();
    setSubdomain(cleanDomain);
    setIsValid(validateSubdomain(cleanDomain));
    // Reset status when domain changes
    if (cleanDomain !== user?.publishing_domain) {
      setConnectionStatus('not_connected');
    }
  };

  const handleConnect = async () => {
    if (!validateSubdomain(subdomain)) {
      toast.error('Please enter a valid subdomain');
      return;
    }

    setSaving(true);
    setConnectionStatus('pending');
    
    try {
      // Save the domain first
      await put('/users/me/publishing-domain', { domain: subdomain });
      
      // Then verify DNS
      await verifyDns();
      
      toast.success('Domain saved! Checking DNS configuration...');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.message || 'Failed to save domain');
      setConnectionStatus('not_connected');
    } finally {
      setSaving(false);
    }
  };

  const verifyDns = async () => {
    if (!subdomain) return;
    
    setVerifying(true);
    try {
      const result = await post('/users/me/verify-domain', { domain: subdomain });
      setConnectionStatus(result.status || 'pending');
      
      if (result.status === 'connected') {
        toast.success('Domain connected successfully!');
      } else if (result.status === 'invalid') {
        toast.error('DNS not configured correctly. Please check your CNAME record.');
      } else {
        toast.info('DNS propagation in progress. This can take up to 24 hours.');
      }
    } catch (error) {
      console.error('DNS verification failed:', error);
      setConnectionStatus('pending');
      toast.info('Unable to verify DNS. Please ensure your CNAME record is configured.');
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const getPublishedUrl = (deck) => {
    if (subdomain && connectionStatus === 'connected') {
      return `https://${subdomain}/${generateSlug(deck.deck_name)}`;
    }
    return `${window.location.origin}${deck.public_url}`;
  };

  const getStatusBadge = () => {
    switch (connectionStatus) {
      case 'connected':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending DNS
          </Badge>
        );
      case 'invalid':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Invalid Configuration
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-semibold mb-2">Connect Your Branded Deck Link</h1>
          <p className="text-muted-foreground">
            Let clients view your Vision Decks on your own branded link (e.g., decks.yourcompany.com)
          </p>
        </div>

        {/* DNS Setup Instructions */}
        <Card className="rounded-sm border-2 border-stone-200">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#B89B5E] text-white text-sm font-bold">1</span>
              Add DNS Record
            </CardTitle>
            <CardDescription>
              Create a CNAME record at your domain provider (GoDaddy, Squarespace, Cloudflare, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* DNS Record Display */}
            <div className="bg-stone-50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Type</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 p-2 bg-white rounded border text-sm font-mono">CNAME</code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => copyToClipboard('CNAME')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Host / Name</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 p-2 bg-white rounded border text-sm font-mono">decks</code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => copyToClipboard('decks')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Value / Points To</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 p-2 bg-white rounded border text-sm font-mono truncate" title={APP_DEPLOYMENT_DOMAIN}>
                      {APP_DEPLOYMENT_DOMAIN}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => copyToClipboard(APP_DEPLOYMENT_DOMAIN)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Helper text */}
              <div className="flex items-start gap-2 pt-2 border-t border-stone-200">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Create a subdomain like <strong>decks</strong>, <strong>proposals</strong>, or <strong>visions</strong> — do NOT use your main domain.
                  <br />
                  Example: If your domain is <em>yourcompany.com</em>, create <em>decks.yourcompany.com</em>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Enter Domain */}
        <Card className="rounded-sm border-2 border-stone-200">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#B89B5E] text-white text-sm font-bold">2</span>
              Enter Your Subdomain
            </CardTitle>
            <CardDescription>
              Enter the full subdomain you created (e.g., decks.yourcompany.com)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subdomain">Your Subdomain</Label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    https://
                  </span>
                  <Input
                    id="subdomain"
                    value={subdomain}
                    onChange={(e) => handleSubdomainChange(e.target.value)}
                    placeholder="decks.yourcompany.com"
                    className={`pl-16 rounded-sm ${!isValid ? 'border-destructive' : ''}`}
                    data-testid="publishing-domain-input"
                  />
                </div>
              </div>
              {!isValid && validationError && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationError}
                </p>
              )}
            </div>

            {/* URL Preview */}
            {subdomain && isValid && (
              <div className="p-3 bg-stone-50 rounded-lg space-y-1">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Your Deck URLs Will Look Like</Label>
                <code className="block p-2 bg-white rounded border text-sm">
                  https://{subdomain}/your-deck-title
                </code>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connect Button */}
        <Card className="rounded-sm border-2 border-stone-200">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#B89B5E] text-white text-sm font-bold">3</span>
              Connect Domain
            </CardTitle>
            <CardDescription>
              Save your domain and verify DNS configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Button 
                onClick={handleConnect} 
                disabled={saving || !isValid || !subdomain}
                className="rounded-sm bg-[#B89B5E] hover:bg-[#A08050]"
                data-testid="connect-domain-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Connect Domain
                  </>
                )}
              </Button>
              
              {subdomain && getStatusBadge()}
              
              {subdomain && connectionStatus !== 'not_connected' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={verifyDns}
                  disabled={verifying}
                  className="ml-auto"
                >
                  {verifying ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Check Status'
                  )}
                </Button>
              )}
            </div>

            {/* Status Info */}
            {connectionStatus === 'connected' && (
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-green-900">Domain Connected!</p>
                  <p className="text-green-700 mt-1">
                    Your Vision Decks are now accessible at <strong>https://{subdomain}</strong>
                  </p>
                </div>
              </div>
            )}

            {connectionStatus === 'pending' && subdomain && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <Clock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-900">DNS Propagation in Progress</p>
                  <p className="text-amber-700 mt-1">
                    DNS changes can take 5-30 minutes to propagate (sometimes up to 24 hours). 
                    Click "Check Status" to verify once your DNS is configured.
                  </p>
                </div>
              </div>
            )}

            {connectionStatus === 'invalid' && (
              <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                <XCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-red-900">DNS Not Configured Correctly</p>
                  <p className="text-red-700 mt-1">
                    Please ensure your CNAME record points to <strong>{APP_DEPLOYMENT_DOMAIN}</strong>.
                    Double-check the Host and Value fields in your DNS settings.
                  </p>
                </div>
              </div>
            )}

            {/* SSL Note */}
            {connectionStatus === 'connected' && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4 text-green-600" />
                <span>SSL certificate automatically secured</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Published Decks */}
        {recentDecks.length > 0 && (
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Your Published Decks</CardTitle>
              <CardDescription>
                {connectionStatus === 'connected' 
                  ? 'Share these branded links with your clients'
                  : 'Use the working link to share your decks now'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentDecks.map((deck) => (
                  <div 
                    key={deck.id} 
                    className="p-4 bg-muted/30 rounded-lg space-y-3"
                  >
                    <p className="font-medium text-sm">{deck.deck_name}</p>
                    
                    {/* Working Link */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600" />
                        <span className="text-xs font-medium text-green-700">
                          {connectionStatus === 'connected' ? 'Branded Link' : 'Working Link'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-green-50 dark:bg-green-950/30 rounded text-xs border border-green-200 truncate">
                          {getPublishedUrl(deck)}
                        </code>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => copyToClipboard(getPublishedUrl(deck))}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => window.open(connectionStatus === 'connected' ? getPublishedUrl(deck) : deck.public_url, '_blank')}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Fallback Link (if connected) */}
                    {connectionStatus === 'connected' && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">Fallback Link (always works)</span>
                        </div>
                        <code className="block p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-500 truncate">
                          {`${window.location.origin}${deck.public_url}`}
                        </code>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
